/* src/contexts/AuthContext.tsx */
import React, { createContext, useCallback, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { api } from '../services/crmApi';
import { initSocket, disconnectSocket, reportPresenceActivity, reportPresenceLogout } from '../services/socket';
import { navigateToProductLocation } from '../utils/productNavigation';
import { syncWebPushSubscriptionSilently, unsubscribeFromWebPushNotifications } from '../utils/webPushNotifications';
// import { DB_PATHS, AVAILABLE_VIEWS } from '../modules/finance/constants';

const STORAGE_KEY = '@VendaPro:User';
const DOCS_HOSTNAMES = new Set(['docs.vendaprocrm.com.br']);

// --- UTILITÁRIOS ---


// --- INTEGRAÇÃO ASAAS (REMOVED - LEGACY) ---

type CompanyModuleMap = Record<string, boolean | undefined>;

export interface SystemUser {
  id: string;
  name: string;
  email: string;
  role: string;
  companyId: string;
  session?: {
    type?: 'standard' | 'impersonation' | string;
    impersonation?: {
      kind?: string | null;
      byUserId?: string | null;
      byUserName?: string | null;
      byUserRole?: string | null;
      byCompanyId?: string | null;
      targetCompanyId?: string | null;
      targetUserId?: string | null;
      reason?: string | null;
      startedAt?: string | null;
    } | null;
  };
  commissionSplit?: number;
  isActive?: boolean;
  planId?: string;
  allowedViews?: string[];
  companyStatus?: 'active' | 'blocked' | 'pending_payment';
  invoiceUrl?: string;
  asaasCustomerId?: string; // ✅ Added to prevent TS errors on BillingAdmin
  company?: {
    name?: string;
    status?: string;
    parentCompanyId?: string;
    companyType?: string;
    vertical?: string;
    terminology?: Record<string, string>;
    canManageSubcompanies?: boolean;
    rootCompanyId?: string;
    rootCompanyName?: string;
    modules?: {
      whatsapp?: boolean;
      marketing?: boolean;
      whiteLabel?: boolean;
      apiAccess?: boolean;
      bankingIntegrations?: boolean;
      bankAccess?: boolean;
      dataEnrichment?: boolean;
      portability?: boolean;
      whatsappAi?: boolean;
      [key: string]: boolean | undefined;
    };
    explicitModules?: string[];
    planModules?: CompanyModuleMap;
    moduleOverrides?: CompanyModuleMap;
    planId?: string;
    planName?: string;
    planMaxUsers?: number;
    planQuizLimit?: number;
    planSubcompanyConfigured?: boolean;
    planSubcompanyLimit?: number;
    subcompanyFeatureEnabled?: boolean;
    commissionSplit?: number;
    sessionPolicy?: {
      idleLogoutEnabled?: boolean;
      idleLogoutMinutes?: number;
    };
  };
  whatsappAvailabilityStatus?: 'available' | 'unavailable' | string;
  whatsappAvailabilityUpdatedAt?: string | null;
}

type AuthErrorField = 'email' | 'password';

interface AuthFailure extends Error {
  code?: string;
  status?: number;
  field?: AuthErrorField;
}

export interface SignUpPayload {
  email: string;
  password: string;
  companyName: string;
  /** Define catálogo semeado, módulos padrão e vocabulário da conta. */
  vertical?: string;
  name: string;
  phone: string;
  cpf: string;
  planId: string;
  coupon?: string;
  subscriptionConfig?: Record<string, unknown>;
  quotedPrice?: number;
}

type ApiErrorShape = {
  code?: string;
  message?: string;
  request?: unknown;
  response?: {
    status?: number;
    data?: {
      error?: string;
      code?: string;
      field?: AuthErrorField;
    };
  };
};

interface AuthContextData {
  signed: boolean;
  user: SystemUser | null;
  loading: boolean;
  authLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (data: SignUpPayload) => Promise<void>;
  signOut: () => Promise<void>;
  updateUser: (data: Partial<SystemUser>) => void;
}

const AuthContext = createContext<AuthContextData | undefined>(undefined);
const SESSION_ACTIVITY_KEY = 'vendapro:last-session-activity-at';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SystemUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const idleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn('Sessão expirada ou 401 recebido. Deslogando usuário...');
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('token');
      localStorage.removeItem('superadmin_token_backup');
      localStorage.removeItem(SESSION_ACTIVITY_KEY);
      setUser(null);
      disconnectSocket();

      const currentHostname = typeof window !== 'undefined'
        ? String(window.location.hostname || '').trim().toLowerCase()
        : '';

      if (DOCS_HOSTNAMES.has(currentHostname)) {
        window.location.assign('/');
        return;
      }

      navigateToProductLocation('/login');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    const bootstrap = async () => {
      const localToken = localStorage.getItem('token');

      if (localToken) {
        try {
          const userProfile = await fetchUserProfile();
          setUser(userProfile);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(userProfile));
          initSocket(localToken);
          void syncWebPushSubscriptionSilently();
          setLoading(false);
          return;
        } catch (error) {
          const statusCode = (error as ApiErrorShape)?.response?.status;
          if (![401, 403, 404].includes(statusCode)) {
            console.error('Erro ao restaurar sessão local:', error);
          }
        }
      }

      setUser(null);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('token');
      localStorage.removeItem('superadmin_token_backup');
      localStorage.removeItem(SESSION_ACTIVITY_KEY);
      disconnectSocket();
      setLoading(false);
    };

    bootstrap();

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const fetchUserProfile = async (): Promise<SystemUser> => {
    // 🟢 MIGRATION: Fetch Profile from SQL Backend
    try {
      const res = await api.get('/api/auth/me');
      const backendUser = res.data;

      return {
        id: backendUser.id,
        name: backendUser.name,
        email: backendUser.email,
        role: backendUser.role,
        companyId: backendUser.company_id,
        commissionSplit: backendUser.commission_split, // Snake case from DB
        allowedViews: backendUser.allowed_views,
        isActive: backendUser.is_active,
        companyStatus: backendUser.companyStatus,
        planId: backendUser.planId,
        invoiceUrl: backendUser.invoiceUrl,
        asaasCustomerId: backendUser.asaasCustomerId,
        whatsappAvailabilityStatus: backendUser.whatsappAvailabilityStatus || backendUser.whatsapp_availability_status || 'available',
        whatsappAvailabilityUpdatedAt: backendUser.whatsappAvailabilityUpdatedAt || backendUser.whatsapp_availability_updated_at || null,
        company: backendUser.company,
        session: backendUser.session || { type: 'standard', impersonation: null }
      } as SystemUser;
    } catch (error: unknown) {
      const apiError = error as ApiErrorShape;
      // Handle "Not Found" or "Unauthorized"
      if (apiError.response?.status === 404) {
        throw new Error('Usuário sem cadastro (SQL).');
      }
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    setAuthLoading(true);
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const response = await api.post('/api/auth/login', {
        email: normalizedEmail,
        password
      });

      const token = response.data?.token;
      if (!token) {
        throw new Error('Token não retornado pelo backend.');
      }

      localStorage.setItem('token', token);

      const userProfile = await fetchUserProfile();
      setUser(userProfile);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userProfile));
      initSocket(token);
      void syncWebPushSubscriptionSilently();
    } catch (error: unknown) {
      localStorage.removeItem('token');
      localStorage.removeItem(SESSION_ACTIVITY_KEY);
      console.error("Erro login:", error);
      const apiError = error as ApiErrorShape;
      const backendMessage = apiError.response?.data?.error;
      const backendCode = apiError.response?.data?.code;
      const backendField = apiError.response?.data?.field;
      const statusCode = apiError.response?.status;
      const isNetworkFailure = !apiError.response && (
        apiError.code === 'ERR_NETWORK'
        || apiError.message === 'Network Error'
        || Boolean(apiError.request)
      );
      const isServiceFailure = isNetworkFailure || (statusCode !== undefined && statusCode >= 500);
      const fallbackMessage = statusCode === 429
        ? 'Muitas tentativas de acesso. Aguarde alguns instantes e tente novamente.'
        : isServiceFailure
          ? 'Não foi possível conectar ao CRM agora. Aguarde alguns instantes e tente novamente.'
          : 'Credenciais inválidas.';
      const authError = new Error(backendMessage || fallbackMessage) as AuthFailure;

      authError.code = backendCode
        || (statusCode === 429
          ? 'auth/too-many-requests'
          : isServiceFailure
            ? 'auth/service-unavailable'
            : undefined);
      authError.status = statusCode;
      authError.field = backendField;

      throw authError;
    } finally {
      setAuthLoading(false);
    }
  };

  const signUp = async (data: SignUpPayload) => {
    setAuthLoading(true);
    try {
      await api.post('/api/auth/register', data);
      await signIn(data.email, data.password);

      // Disparo de Conversão do Meta Pixel (Purchase) na conclusão do cadastro
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        const planPurchaseValues: Record<string, number> = {
          'crm-operacional': 297,
          'crm-whatsapp': 497,
          'crm-rede': 697
        };
        const quotedValue = Number(data.quotedPrice || 0);
        const value = Number.isFinite(quotedValue) && quotedValue > 0 ? quotedValue : (planPurchaseValues[data.planId] ?? 297);
        window.fbq('track', 'Purchase', { currency: 'BRL', value: value });
      }

    } catch (error: unknown) {
      const apiError = error as ApiErrorShape;
      console.error("Erro Cadastro:", error);
      console.error("Detalhe do Erro Backend:", apiError.response?.data || apiError.message);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  };

  const endSession = useCallback(async (reason: 'explicit_logout' | 'idle_timeout') => {
    reportPresenceLogout(reason);
    await Promise.allSettled([
      unsubscribeFromWebPushNotifications(),
      api.post('/api/auth/logout', { reason })
    ]);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('token');
    localStorage.removeItem('superadmin_token_backup');
    localStorage.removeItem(SESSION_ACTIVITY_KEY);
    setUser(null);
    disconnectSocket();
    navigateToProductLocation('/login');
  }, []);
  const signOut = useCallback(() => endSession('explicit_logout'), [endSession]);

  useEffect(() => {
    if (!user) return undefined;

    const sessionPolicy = user.company?.sessionPolicy;
    const idleLogoutEnabled = sessionPolicy?.idleLogoutEnabled === true;
    const idleLogoutMinutes = Math.max(5, Number(sessionPolicy?.idleLogoutMinutes || 30));
    const idleTimeoutMs = idleLogoutMinutes * 60 * 1000;
    let lastActivityAt = Date.now();
    let lastPersistedActivityAt = 0;
    let lastReportedPath = '';

    const currentScreen = () => {
      if (typeof window === 'undefined') return 'CRM';
      return `${window.location.pathname}${window.location.search || ''}` || 'CRM';
    };

    const clearIdleTimer = () => {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    const scheduleIdleLogout = () => {
      clearIdleTimer();
      if (!idleLogoutEnabled) return;

      const remainingMs = Math.max(0, idleTimeoutMs - (Date.now() - lastActivityAt));
      idleTimerRef.current = window.setTimeout(() => {
        void endSession('idle_timeout');
      }, remainingMs);
    };

    const persistActivity = (timestamp: number, force = false) => {
      if (!force && timestamp - lastPersistedActivityAt < 5000) return;
      lastPersistedActivityAt = timestamp;
      localStorage.setItem(SESSION_ACTIVITY_KEY, String(timestamp));
    };

    const reportCurrentScreen = (action?: string) => {
      const screen = currentScreen();
      const screenChanged = screen !== lastReportedPath;
      if (screenChanged) lastReportedPath = screen;
      reportPresenceActivity({
        screen,
        action: action || (screenChanged ? `Acessou ${screen}` : undefined),
        force: screenChanged
      });
    };

    const handleActivity = () => {
      lastActivityAt = Date.now();
      persistActivity(lastActivityAt);
      reportPresenceActivity({});
      scheduleIdleLogout();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleActivity();
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === SESSION_ACTIVITY_KEY && event.newValue) {
        const sharedActivityAt = Number(event.newValue);
        if (Number.isFinite(sharedActivityAt) && sharedActivityAt > lastActivityAt) {
          lastActivityAt = sharedActivityAt;
          scheduleIdleLogout();
        }
      }

      if (event.key === 'token' && event.newValue === null) {
        setUser(null);
        disconnectSocket();
        navigateToProductLocation('/login');
      }
    };

    const activityEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, handleActivity, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);
    persistActivity(lastActivityAt, true);
    reportCurrentScreen('Entrou no CRM');
    scheduleIdleLogout();

    const heartbeat = window.setInterval(() => {
      reportPresenceActivity({});
    }, 30000);

    return () => {
      clearIdleTimer();
      window.clearInterval(heartbeat);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, handleActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [endSession, user]);

  const updateUser = (data: Partial<SystemUser>) => {
    if (user) {
      const updated = { ...user, ...data };
      setUser(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
  };

  return (
    <AuthContext.Provider value={{ signed: !!user, user, loading, authLoading, signIn, signUp, signOut, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  return context;
};
